import { useCallback, useEffect, useState } from "react";

import {
  ApiError,
  passkeys,
  type StaffPasskeySummary,
} from "../api/staff";
import {
  createPasskey,
  isPasskeySupported,
  passkeyErrorName,
} from "../api/webauthn";

function fmtDate(iso: string | null): string {
  if (!iso) return "never";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

function deviceTypeLabel(p: StaffPasskeySummary): string {
  if (p.backedUp) return "Synced";
  if (p.credentialDeviceType === "multiDevice") return "Multi-device";
  if (p.transports?.includes("usb")) return "Hardware key";
  if (p.transports?.includes("internal")) return "This device";
  return "Passkey";
}

export function Passkeys() {
  const [items, setItems] = useState<StaffPasskeySummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [supported, setSupported] = useState(false);

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [createPending, setCreatePending] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  useEffect(() => {
    void isPasskeySupported().then(setSupported);
  }, []);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await passkeys.list();
      setItems(res.items);
    } catch (err) {
      setError(describeError(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleCreate() {
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setCreatePending(true);
    setError(null);
    try {
      const { challengeId, options } = await passkeys.registerOptions(
        name.trim(),
      );
      const attestation = await createPasskey(options);
      await passkeys.registerVerify(challengeId, attestation);
      setName("");
      setCreating(false);
      await refresh();
    } catch (err) {
      const n = passkeyErrorName(err);
      if (n === "NotAllowedError" || n === "AbortError") return;
      setError(describeError(err));
    } finally {
      setCreatePending(false);
    }
  }

  async function handleRevoke(id: string) {
    const ok = confirm(
      "Remove this staff passkey? You won't be able to sign in with it again.",
    );
    if (!ok) return;
    setPendingId(id);
    setError(null);
    try {
      await passkeys.revoke(id);
      await refresh();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setPendingId(null);
    }
  }

  async function handleRename(id: string) {
    if (!renameValue.trim()) return;
    setPendingId(id);
    setError(null);
    try {
      await passkeys.rename(id, renameValue.trim());
      setRenameId(null);
      setRenameValue("");
      await refresh();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setPendingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Staff passkeys</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Add Face ID / Touch ID / Windows Hello / hardware-key passkeys to
          your staff identity. Passkeys can sign in to the back-office without
          a session token.
        </p>
      </div>

      <div className="card space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium uppercase tracking-wide text-text-secondary">
            Your passkeys
          </h2>
          {!creating && (
            <button
              type="button"
              className="btn-primary"
              disabled={!supported}
              title={supported ? undefined : "Your browser doesn't support passkeys."}
              onClick={() => {
                setCreating(true);
                setError(null);
              }}
            >
              Add passkey
            </button>
          )}
        </div>

        {error ? (
          <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {error}
          </div>
        ) : null}

        {creating && (
          <div className="space-y-3 rounded-md border border-border bg-bg-subtle p-3">
            <div>
              <label className="label">Name</label>
              <input
                autoFocus
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="MacBook (Touch ID)"
                maxLength={64}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  setCreating(false);
                  setName("");
                  setError(null);
                }}
                disabled={createPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => void handleCreate()}
                disabled={createPending || !name.trim() || !supported}
              >
                {createPending ? "Adding…" : "Create"}
              </button>
            </div>
          </div>
        )}

        {items === null && !error && (
          <p className="text-sm text-text-muted">Loading…</p>
        )}
        {items && items.length === 0 && (
          <p className="text-sm text-text-muted">No passkeys yet.</p>
        )}
        {items && items.length > 0 && (
          <ul className="divide-y divide-border">
            {items.map((p) => {
              const renaming = renameId === p.id;
              return (
                <li
                  key={p.id}
                  className="flex flex-wrap items-start justify-between gap-3 py-3"
                >
                  <div className="min-w-0 space-y-0.5">
                    {renaming ? (
                      <div className="flex items-center gap-2">
                        <input
                          className="input"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          maxLength={64}
                          autoFocus
                        />
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={() => void handleRename(p.id)}
                          disabled={!renameValue.trim() || pendingId === p.id}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="btn-ghost"
                          onClick={() => {
                            setRenameId(null);
                            setRenameValue("");
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-medium">{p.name}</span>
                        <span className="rounded-full bg-bg px-2 py-0.5 text-xs text-text-muted">
                          {deviceTypeLabel(p)}
                        </span>
                      </div>
                    )}
                    <div className="text-xs text-text-muted">
                      created {fmtDate(p.createdAt)} · last used{" "}
                      {fmtDate(p.lastUsedAt)}
                    </div>
                  </div>
                  {!renaming && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => {
                          setRenameId(p.id);
                          setRenameValue(p.name);
                        }}
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => void handleRevoke(p.id)}
                        disabled={pendingId === p.id}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
